import { useEffect } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { Header } from "./Header";
import { Footer } from "./Footer";
import { MobileBookingBar, useIsMobile } from "@/components/booking/BookingBar";
import { useLang } from "@/lib/i18n";

/** Scrolls to the top on navigation – or to the #hash target once the (async) content has rendered. */
export const ScrollToTop = () => {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (!hash) { window.scrollTo({ top: 0, behavior: "instant" }); return undefined; }
    const id = decodeURIComponent(hash.slice(1));
    let tries = 0;
    const timers = [];
    const scrollTo = (el, behavior) => el.scrollIntoView({ behavior, block: "start" });
    const attempt = () => {
      const el = document.getElementById(id);
      if (el) {
        scrollTo(el, "instant");
        // Content above the target may still settle (fonts, async data) – correct the position a couple of times
        [350, 900].forEach((ms) => timers.push(setTimeout(() => {
          const t = document.getElementById(id);
          if (!t) return;
          const top = t.getBoundingClientRect().top;
          const offset = parseFloat(getComputedStyle(t).scrollMarginTop) || 0;
          if (Math.abs(top - offset) > 12) scrollTo(t, "instant");
        }, ms)));
        return;
      }
      if (tries++ < 20) timers.push(setTimeout(attempt, 120));
    };
    attempt();
    return () => timers.forEach(clearTimeout);
  }, [pathname, hash]);
  return null;
};

/** Pages whose first screen is a full-bleed photo – the header sits transparent on top of it */
const PHOTO_HERO_PAGES = ["/rom", "/mat", "/opplev", "/arrangement", "/praktisk", "/om-oss"];

export const SiteLayout = () => {
  const { pathname } = useLocation();
  const isMobile = useIsMobile();
  const bookingFlow = pathname.startsWith("/bestill") || pathname.startsWith("/mi-bestilling");
  const showSearch = pathname === "/" || pathname === "/rom";
  // Full-bleed photo first screen on the home page and all content pages – the header floats on top
  const overlay = pathname === "/" || PHOTO_HERO_PAGES.includes(pathname);
  const { tx } = useLang();
  return (
    <div className="min-h-screen bg-background">
      <a href="#main" className="skip-link" data-testid="skip-link">{tx("Hopp til innhaldet", "Skip to content")}</a>
      <Header showSearch={showSearch} overlay={overlay} />
      <main id="main" className={overlay ? "" : "pt-16 lg:pt-[72px]"}>
        <div key={pathname} className="page-enter">
          <Outlet />
        </div>
      </main>
      <Footer compact={bookingFlow} />
      {!bookingFlow && <MobileBookingBar />}
    </div>
  );
};
